import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQuery } from '@apollo/client';
import { map, padStart, uniq, upperCase, without, defaultsDeep, omit, upperFirst, isEqual, startCase, camelCase, defaults } from 'lodash';
import moment from 'moment';
import {
  ui,
  uiA,
  uiFormRow,
  uiInputText,
  uiSelect,
  uiButtonSwitch,
  uiCalendar,
  uiCheckbox,
  uiModal,
  uiMerge,
  uiButton,
  uiCard,
  uiMenuFolder,
  uiBadge,
  uiAlert,
} from 'envy-ui';
import ReactModal from 'react-modal';
import { ToggleLayer, anchor } from 'react-laag';
import { SET_CLIENT_STATE, UPDATE_GENERAL_SETTINGS, GENERAL_SETTINGS, LAST_SYNC_DATE } from '../../../graphqls';
import getScheduledInfo from '../../../util/getScheduledInfo';
import getUserId from '../../../util/getUserId';

export default function RequestSync({ startPolling, stopPolling }) {
  // hooks
  const formRef = useRef(null);
  const [autoPublish, setAutoPublish] = useState(true);
  const [autoSync, setAutoSync] = useState(false);
  const [autoType, setAutoType] = useState('KEEP');
  const [confirmed, setConfirmed] = useState(false);
  const [frequency, setFrequency] = useState('REPORTING_DATE');
  const [menuVisible, setMenuVisible] = useState(false);
  const [meridiem, setMeridiem] = useState('AM');
  const [monthlyDates, setMonthlyDates] = useState([1]);
  const [monthlyDay, setMonthlyDay] = useState('DAY');
  const [monthlyEvery, setMonthlyEvery] = useState(1);
  const [monthlyFirstLast, setMonthlyFirstLast] = useState('FIRST');
  const [monthlyMode, setMonthlyMode] = useState('EACH');
  const [reportingBeforeAfter, setReportingBeforeAfter] = useState('AFTER');
  const [reportingDays, setReportingDays] = useState(1);
  const [syncMode, setSyncMode] = useState(true); // default to sync now
  const [syncStartOn, setSyncStartOn] = useState(moment().format('yyyy-MM-DD'));
  const [syncType, setSyncType] = useState('KEEP');
  const [timeGenerated, setTimeGenerated] = useState(7);
  const [weeklyEvery, setWeeklyEvery] = useState(1);
  const [weeklyOn, setWeeklyOn] = useState([1]);
  const [withPublish, setWithPublish] = useState(true);
  const [withChanges, setWithChanges] = useState(false);
  const [withLabel, setWithLabel] = useState(false);
  const [polling, setPolling] = useState(false);
  const [notificationType, setNotificationType] = useState('ERROR');
  const menuRef = useRef(null);
  const {
    loading,
    data: {
      generalSettings: {
        syncStatus,
        syncSettings = {},
        timeZoneName,
        reportingDates,
        syncStartOn: initSyncStartOn,
        syncSettings: {
          autoSync: autoSynced = false,
          syncType: defaultSyncType,
          amPmGenerated: initAmPmGenerated,
          frequency: initFrequency,
          monthlyEachDates: initMonthlyEachDates,
          monthlyEvery: initMonthlyEvery,
          monthlyMode: initMonthlyMode,
          monthlyOnTheDayOption: initMonthlyOnTheDayOption,
          monthlyOnTheFirstLastOption: initMonthlyOnTheFirstLastOption,
          reportingDateBeforeAfter: initReportingDateBeforeAfter,
          reportingDateDays: initReportingDateDays,
          timeGenerated: initTimeGenerated,
          weeklyEvery: initWeeklyEvery,
          weeklyOn: initWeeklyOn,
        } = {},
        notificationType: initNotificationType,
      } = {},
    } = {},
  } = useQuery(GENERAL_SETTINGS, {
    onCompleted: ({
      generalSettings: {
        syncStartOn: defaultSyncStartOn = moment().format('yyyy-MM-DD'),
        syncSettings: {
          amPmGenerated: defaultAmPmGenerated,
          autoSync: defaultAuto,
          frequency: defaultFrequency,
          monthlyEachDates: defaultMonthlyEachDates,
          monthlyEvery: defaultMonthlyEvery,
          monthlyMode: defaultMonthlyMode,
          monthlyOnTheDayOption: defaultMonthlyOnTheDayOption,
          monthlyOnTheFirstLastOption: defaultMonthlyOnTheFirstLastOption,
          reportingDateBeforeAfter: defaultReportingDateBeforeAfter,
          reportingDateDays: defaultReportingDateDays,
          syncType: defaultType,
          timeGenerated: defaultTimeGenerated,
          weeklyEvery: defaultWeeklyEvery,
          weeklyOn: defaultWeeklyOn,
        },
        notificationType: defaultNotificationType,
      },
    }) => {
      setAutoSync(defaultAuto || false);
      setFrequency(defaultFrequency === 'REPORTING_DATE' ? (reportingDates?.length ? defaultFrequency : 'DAILY') : defaultFrequency);
      setMeridiem(defaultAmPmGenerated || 'AM');
      setMonthlyDates(defaultMonthlyEachDates || [1]);
      setMonthlyDay(defaultMonthlyOnTheDayOption || 'DAY');
      setMonthlyEvery(defaultMonthlyEvery ?? 1);
      setMonthlyFirstLast(defaultMonthlyOnTheFirstLastOption || 'FIRST');
      setMonthlyMode(defaultMonthlyMode || 'EACH');
      setReportingBeforeAfter(defaultReportingDateBeforeAfter || 'AFTER');
      setReportingDays(defaultReportingDateDays ?? 3);
      setSyncStartOn(
        moment(defaultSyncStartOn).isValid() ? moment(defaultSyncStartOn).format('yyyy-MM-DD') : moment().format('yyyy-MM-DD'),
      );
      setAutoPublish((defaultType || 'KEEP_WITHOUT_PUBLISH').includes('_WITH_'));
      setAutoType((defaultType || 'KEEP_WITHOUT_PUBLISH').replace(/_WITH.*$/, '').replace('_LABEL', ''));
      setWithLabel(defaultType.includes('_LABEL_'));
      setTimeGenerated(defaultTimeGenerated ?? 7);
      setWeeklyEvery(defaultWeeklyEvery ?? 1);
      setWeeklyOn(defaultWeeklyOn || [1]);
      setSyncMode(!defaultAuto);
      setNotificationType(defaultNotificationType);
    },
  });
  const { refetch } = useQuery(LAST_SYNC_DATE);
  const [setState] = useMutation(SET_CLIENT_STATE);
  const [updateGeneralSettings, { loading: updatingGeneralSettings }] = useMutation(UPDATE_GENERAL_SETTINGS, {
    refetchQueries: [{ query: GENERAL_SETTINGS }],
    awaitRefetchQueries: true,
    onCompleted() {
      setWithChanges(false);
      startPolling(3000);
      setPolling(true);
    },
  });

  useEffect(() => {
    if (!syncStatus) {
      stopPolling();
      setTimeout(() => {
        if (polling) {
          setState({ variables: { modalTypeShown: '' } });
        }
      }, 3000);
      setPolling(false);
      refetch();
    }
  }, [syncStatus]);

  useEffect(() => {
    onFormChange();
  }, [
    autoPublish,
    autoSync,
    autoType,
    confirmed,
    frequency,
    menuVisible,
    meridiem,
    monthlyDates,
    monthlyDay,
    monthlyEvery,
    monthlyFirstLast,
    monthlyMode,
    reportingBeforeAfter,
    reportingDays,
    syncMode,
    syncStartOn,
    syncType,
    timeGenerated,
    weeklyEvery,
    weeklyOn,
    withPublish,
    notificationType,
  ]);

  // actions
  const onFormChange = () => {
    if (formRef?.current) {
      const formData = new FormData(formRef.current);
      const {
        meridiem: meridiemValue,
        monthlyDay: monthlyDayValue,
        monthlyEvery: monthlyEveryValue,
        monthlyFirstLast: monthlyFirstLastValue,
        reportingDateBeforeAfter: reportingDateBeforeAfterValue,
        reportingDays: reportingDaysValue,
        timeGenerated: timeGeneratedValue,
        weeklyEvery: weeklyEveryValue,
        notificationType: notificationTypeValue,
      } = Object.fromEntries(formData.entries());

      setWithChanges(
        !isEqual(
          {
            amPmGenerated: initAmPmGenerated,
            autoPublish: defaultSyncType.includes('_WITH_'),
            autoSync: autoSynced,
            frequency: initFrequency,
            monthlyEachDates: initMonthlyEachDates,
            monthlyEvery: initMonthlyEvery,
            monthlyMode: initMonthlyMode,
            monthlyOnTheDayOption: initMonthlyOnTheDayOption,
            monthlyOnTheFirstLastOption: initMonthlyOnTheFirstLastOption,
            reportingDateBeforeAfter: initReportingDateBeforeAfter,
            reportingDateDays: initReportingDateDays,
            syncStartOn: moment(initSyncStartOn).format('yyyy-MM-DD'),
            syncType: defaultSyncType.replace(/_WITH.*$/, ''),
            timeGenerated: initTimeGenerated,
            weeklyEvery: initWeeklyEvery,
            weeklyOn: initWeeklyOn,
            notificationType: initNotificationType,
          },
          {
            amPmGenerated: meridiemValue ?? initAmPmGenerated,
            autoPublish,
            autoSync,
            frequency,
            monthlyEachDates: [...(monthlyDates || [])] ?? initMonthlyEachDates,
            monthlyEvery: monthlyEveryValue ? Number(monthlyEveryValue) : initMonthlyEvery,
            monthlyMode,
            monthlyOnTheDayOption: monthlyDayValue ?? initMonthlyOnTheDayOption,
            monthlyOnTheFirstLastOption: monthlyFirstLastValue ?? initMonthlyOnTheFirstLastOption,
            reportingDateBeforeAfter: reportingDateBeforeAfterValue ?? initReportingDateBeforeAfter,
            reportingDateDays: reportingDaysValue ? Number(reportingDaysValue) : initReportingDateDays,
            syncStartOn,
            syncType: syncMode ? syncType : autoType,
            timeGenerated: timeGeneratedValue ? Number(timeGeneratedValue) : initTimeGenerated,
            weeklyEvery: weeklyEveryValue ? Number(weeklyEveryValue) : initWeeklyEvery,
            weeklyOn,
            notificationType: notificationTypeValue ?? initNotificationType,
          },
        ),
      );
    }
  };
  const onTogglePublish = () => (syncMode ? setWithPublish(!withPublish) : setAutoPublish(!autoPublish));
  const onSwitchType = ({ currentTarget: { value } }) => {
    if (syncMode) {
      setSyncType(value);
    } else {
      setAutoType(value);
    }

    if (value === 'SYNC_ONLY') {
      setWithPublish(false);
    }

    setWithLabel(false);
  };
  const onToggleMenu = () => setMenuVisible(!menuVisible);
  const onToggleAutoSync = () => {
    if (!autoSync) {
      setAutoType('KEEP');
      setSyncStartOn(moment().format('yyyy-MM-DD'));
    } else {
      const userId = getUserId(window.userId);
      if (!userId) {
        return console.error('userId should exist');
      }
      updateGeneralSettings({
        variables: {
          syncSettings: defaultsDeep(
            {
              autoSync: false,
              syncType: 'KEEP_WITH_PUBLISH',
              userId,
            },
            syncSettings,
          ),
        },
      });
    }

    setAutoSync(!autoSync);
  };
  const onConfirm = (event) => {
    event.preventDefault();

    const {
      target: {
        meridiem: { value: meridiemValue } = {},
        monthlyDay: { value: monthlyDayValue } = {},
        monthlyEvery: { value: monthlyEveryValue } = {},
        monthlyFirstLast: { value: monthlyFirstLastValue } = {},
        reportingDateBeforeAfter: { value: reportingDateBeforeAfterValue } = {},
        reportingDays: { value: reportingDaysValue } = {},
        timeGenerated: { value: timeGeneratedValue } = {},
        weeklyEvery: { value: weeklyEveryValue } = {},
        notificationType: { value: notificationTypeValue } = {},
      },
    } = event;

    const userId = getUserId(window.userId);
    if (!userId) {
      return console.error('userId should exist');
    }

    if (syncMode) {
      setConfirmed(true);
    } else if (!withChanges) {
      alert('nothing changed');

      return;
    }

    updateGeneralSettings({
      variables: {
        syncStatus: syncMode,
        syncType: `${syncType}${withLabel && syncType === 'OVERWRITE' ? '_LABEL' : ''}${
          syncType !== 'SYNC_ONLY' ? (withPublish ? '_WITH_PUBLISH' : '_WITHOUT_PUBLISH') : ''
        }`,
        syncSettings: defaults(
          {
            amPmGenerated: meridiemValue,
            autoSync,
            frequency,
            monthlyEachDates: [...(monthlyDates || [])],
            monthlyEvery: Number(monthlyEveryValue || 1),
            monthlyMode,
            monthlyOnTheDayOption: monthlyDayValue,
            monthlyOnTheFirstLastOption: monthlyFirstLastValue,
            reportingDateBeforeAfter: reportingDateBeforeAfterValue,
            reportingDays: Number(reportingDaysValue) ?? 1,
            syncType: `${autoType}${autoPublish ? '_WITH_PUBLISH' : '_WITHOUT_PUBLISH'}`,
            timeGenerated: Number(timeGeneratedValue) || 7,
            weeklyEvery: Number(weeklyEveryValue) || 1,
            weeklyOn,
          },
          syncSettings,
        ),
        syncStartOn,
        notificationType: notificationTypeValue,
        userId,
      },
    });
  };
  const onClose = () => setState({ variables: { modalTypeShown: '' } });
  const onToggleSyncMode = () => {
    if (syncMode) {
      const initSyncType = defaultSyncType || 'KEEP_WITH_PUBLISH';

      setAutoType(initSyncType.replace(/_WITH.*/, ''));
      setAutoPublish(initSyncType.includes('_WITH_'));
    }

    setSyncMode(!syncMode);
  };
  const onSwitchFrequency = ({ currentTarget: { value } }) => {
    setFrequency(value);
  };
  const onToggleWeeklyOn = ({ currentTarget: { value } }) => {
    setWeeklyOn(
      weeklyOn.includes(Number(value)) && weeklyOn.length > 1 ? without(weeklyOn, Number(value)) : uniq([...weeklyOn, Number(value)]),
    );
  };
  const onToggleMonthlyDates = ({ currentTarget: { value } }) => {
    setMonthlyDates(
      monthlyDates.includes(Number(value)) && monthlyDates.length > 1
        ? without(monthlyDates, Number(value))
        : uniq([...monthlyDates, Number(value)]),
    );
  };
  const onSwitchMonthlyMode = ({ currentTarget: { value } }) => setMonthlyMode(value);
  const onChangeSyncStartOn = ({ currentTarget: { value } }) => setSyncStartOn(moment(value).format('yyyy-MM-DD'));
  const onToggleLabel = () => setWithLabel(!withLabel);
  const onChangeNotificationType = ({ currentTarget: { value } }) => setNotificationType(value);

  if (loading) {
    return '';
  }

  let nextMoment = moment();
  let humanizedString = '';
  ({ nextMoment, humanizedString } = getScheduledInfo({
    startOn: initSyncStartOn,
    amPmGenerated: initAmPmGenerated,
    frequency: initFrequency,
    monthlyMode: initMonthlyMode,
    monthlyEvery: initMonthlyEvery,
    monthlyEachDates: initMonthlyEachDates,
    monthlyOnTheDayOption: initMonthlyOnTheDayOption,
    monthlyOnTheFirstLastOption: initMonthlyOnTheFirstLastOption,
    reportingDateDays: initReportingDateDays,
    reportingDateBeforeAfter: initReportingDateBeforeAfter,
    timeGenerated: initTimeGenerated,
    weeklyEvery: initWeeklyEvery,
    weeklyOn: initWeeklyOn,
    reportingDates,
  }));

  return (
    <ReactModal
      isOpen
      portalClassName={uiMerge([uiModal``])}
      overlayClassName={uiMerge([uiModal`__overlay`])}
      {...ui([uiModal`__modal`, uiA`overflow-hidden h-800:${!syncMode && autoSync}`])}
    >
      {confirmed ? (
        <div {...ui([uiA`f f-column f-gap-large f-a-center f-j-center h-200`])}>
          <span {...ui([uiA`f f-column f-gap-large f-j-center`])}>
            {!syncStatus && <FontAwesomeIcon icon={['fas', 'check']} {...ui([uiA`font-size-24 color-green`])} />}
            <p>{syncStatus ? 'Sync Request Submitted ...' : 'Sync Success'}</p>
          </span>
          <button type="button" onClick={onClose} {...ui([uiButton`--default`])}>
            <span {...ui([uiButton`__content`])}>Close</span>
          </button>
        </div>
      ) : (
        <>
          <div {...ui([uiModal`__header`])}>
            <div {...ui([uiA`m-right-auto ellipsis`])}>Sync Data</div>
            {!'If we have backup available' && (
              <span ref={menuRef} {...ui([uiA`f f-a-center`])}>
                <ToggleLayer
                  autoAdjust
                  fixed
                  container={() => menuRef.current}
                  isOpen={menuVisible}
                  onOutsideClick={onToggleMenu}
                  placement={{
                    anchor: anchor.BOTTOM_RIGHT,
                    autoAdjust: true,
                    possibleAnchors: [anchor.BOTTOM_LEFT, anchor.BOTTOM_RIGHT, anchor.TOP_CENTER, anchor.BOTTOM_CENTER],
                    snapToAnchor: true,
                    triggerOffset: 5,
                  }}
                  renderLayer={({ isOpen, layerProps }) =>
                    isOpen && (
                      <div ref={layerProps.ref} style={{ ...layerProps.style, zIndex: 5 }}>
                        <ul {...ui([uiCard``, uiA`p-none`])}>
                          <li {...ui([uiA`f f-column`])}>
                            <button
                              type="button"
                              onClick={'onRestore'}
                              {...ui([uiButton`--clean --menu-top --hover-menu`, uiA`color-default`])}
                            >
                              <span {...ui([uiButton`__content`, uiA`p-right-25`])}>
                                Restore Previous Sync&nbsp;<span {...ui([uiA`color-medium-gray`])}>(July 9, 2023)</span>
                              </span>
                            </button>
                          </li>
                        </ul>
                      </div>
                    )
                  }
                >
                  {({ triggerRef }) => (
                    <button ref={triggerRef} onClick={onToggleMenu} type="button" {...ui([uiButton`--clean --content-inverse`])}>
                      <span {...ui([uiButton`__content`])}>
                        <FontAwesomeIcon icon={['far', 'ellipsis-v']} {...ui([uiA`font-size-20`])} />
                      </span>
                    </button>
                  )}
                </ToggleLayer>
                <div {...ui([uiA`h-25 w-1 border-left-panel m-x opacity-5`])} />
              </span>
            )}
            <button type="button" onClick={onClose} {...ui([uiButton`--clean --content-inverse`, uiA`m-right`])}>
              <span {...ui([uiButton`__content`])}>
                <FontAwesomeIcon icon={['fal', 'times']} />
              </span>
            </button>
          </div>
          <div {...ui([uiMenuFolder`--line`, uiA`m-top`])}>
            <button type="button" onClick={onToggleSyncMode} {...ui([uiMenuFolder`__button --active:${syncMode}`])}>
              <span {...ui([uiMenuFolder`__content`])} tabIndex="-1">
                Sync Now
              </span>
            </button>
            <button type="button" onClick={onToggleSyncMode} {...ui([uiMenuFolder`__button --active:${!syncMode}`])}>
              <span {...ui([uiMenuFolder`__content`, uiA`f-gap-small`])} tabIndex="-1">
                <span>Sync Scheduler</span>
                {autoSynced && <span {...ui([uiBadge`--counter`, uiA`color-bg-green font-weight-normal font-size-11`])}>ON</span>}
              </span>
            </button>
          </div>
          <form
            {...ui([uiA`f f-column f-1-1-auto overflow-hidden h-400:${!(!syncMode && autoSync)} h-max-100vh`])}
            onSubmit={onConfirm}
            onChange={onFormChange}
            ref={formRef}
          >
            <div {...ui([uiModal`__content`])}>
              {!syncMode && (
                <>
                  <label
                    {...ui([
                      uiFormRow`--full`,
                      uiA`f-gap f-a-start text-default font-weight-normal w-max-425 m-x m-top-25 m-bottom-75:m-bottom-20${!autoSync}`,
                    ])}
                  >
                    <div {...ui([uiFormRow`__label`, uiA`w-max-75`])}>Schedule</div>
                    <input type="checkbox" checked={autoSync} onChange={onToggleAutoSync} {...ui([uiCheckbox`--toggle`])} />
                  </label>
                  {autoSync && (
                    <p {...ui([uiA`m-x m-y-large`])}>
                      <span {...ui([uiAlert`--warning`, uiA`f f-column f-a-start font-size-14 m-bottom-small line-height-normal`])}>
                        <span {...ui([uiA`semibold`])}>{`Next run: ${nextMoment?.format('lll')}`}</span>
                        <span {...ui([uiA`font-size-12`])}>{`${startCase(camelCase(initFrequency))}${
                          humanizedString ? ` ${humanizedString}` : ''
                        }`}</span>
                      </span>
                    </p>
                  )}
                </>
              )}
              {(syncMode || autoSync) && (
                <>
                  <p {...ui([uiFormRow``, uiA`m-x m-top`])}>
                    {syncMode
                      ? "Select an option to initiate a sync of your Plans data to the Public Dashboard. You'll be notified once the sync is done. The choices are:"
                      : "Select an option to schedule a sync of your Plans data to the Public Dashboard. You'll be notified once the sync is done. The choices are:"}
                  </p>
                  <div {...ui([uiA`f-0-0-auto`])}>
                    <ul {...ui([uiA`m-x p-none`])} style={{ listStyle: 'none' }}>
                      <li>
                        <label {...ui([uiFormRow``, uiA`f f-gap f-a-start font-weight-normal`])}>
                          <input
                            type="radio"
                            value="KEEP"
                            checked={syncMode ? syncType === 'KEEP' : autoType === 'KEEP'}
                            onChange={onSwitchType}
                            {...ui([uiCheckbox`--radio`])}
                          />
                          <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-350`])}>
                            Retain descriptions and only replace plan element updates with data from Plans.
                          </span>
                        </label>
                      </li>
                      <li>
                        <label
                          {...ui([
                            uiFormRow``,
                            uiA`f f-gap f-a-start font-weight-normal m-bottom-small:${syncType === 'OVERWRITE' && syncMode}`,
                          ])}
                        >
                          <input
                            type="radio"
                            value="OVERWRITE"
                            checked={syncMode ? syncType === 'OVERWRITE' : autoType === 'OVERWRITE'}
                            onChange={onSwitchType}
                            {...ui([uiCheckbox`--radio`])}
                          />
                          <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-350`])}>
                            Replace all plan element descriptions and updates with data from Plans.
                          </span>
                        </label>
                        {syncType === 'OVERWRITE' && syncMode && (
                          <label {...ui([uiFormRow``, uiA`f f-gap-small f-a-start font-weight-normal m-left-25 p-left-2`])}>
                            <input
                              type="checkbox"
                              value="LABEL"
                              checked={withLabel}
                              onChange={onToggleLabel}
                              {...ui([uiCheckbox`--check`])}
                            />
                            <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-350 italic`])}>
                              Override navigation labels with plan element descriptions
                            </span>
                          </label>
                        )}
                      </li>
                      {syncMode && (
                        <li>
                          <label {...ui([uiFormRow``, uiA`f f-gap font-weight-normal`])}>
                            <input
                              type="radio"
                              value="SYNC_ONLY"
                              checked={syncType === 'SYNC_ONLY'}
                              onChange={onSwitchType}
                              {...ui([uiCheckbox`--radio`])}
                            />
                            <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-375`])}>
                              Choose specific plan elements for application
                            </span>
                          </label>
                        </li>
                      )}
                    </ul>
                  </div>
                  {(!syncMode || syncType !== 'SYNC_ONLY') && (
                    <>
                      <span {...ui([uiA`f h-1 h-min-1 border-top-light w-100% m-y-large`])} />
                      <label {...ui([uiFormRow`--full`, uiA`f-gap f-a-start f-1-1-auto text-default font-weight-normal w-max-425 m-x`])}>
                        <input
                          type="checkbox"
                          checked={syncMode ? withPublish : autoPublish}
                          onChange={onTogglePublish}
                          {...ui([uiCheckbox`--toggle`])}
                        />
                        <div {...ui([uiFormRow`__label --free `, uiA`f f-column`])}>
                          <span>Publish</span>
                          {((syncMode && withPublish) || (!syncMode && autoPublish)) && (
                            <p {...ui([uiA`w-max-375 font-size-13 m-top-small`])}>
                              All changes will be published and made accessible to the public. This action cannot be reversed.
                              <span {...ui([uiA`color-red`])}>Are you sure?</span>
                            </p>
                          )}
                        </div>
                      </label>
                      {!syncMode && <span {...ui([uiA`f h-1 h-min-1 border-top-light w-100% m-bottom m-top-small`])} />}
                    </>
                  )}
                </>
              )}
              {!syncMode && autoSync && (
                <>
                  <div {...ui([uiA`m`])}>
                    <div {...ui([uiFormRow`--full`, uiA``])}>
                      <span {...ui([uiFormRow`__label`, uiA`w-max-75`])}>Start On</span>
                      <div {...ui([uiA`f f-1-1-auto f-gap-large`])}>
                        <label {...ui([uiFormRow``, uiA`f m-bottom-none f-gap-small f-0-0-135 font-weight-normal`])}>
                          <input type="date" value={syncStartOn} onChange={onChangeSyncStartOn} {...ui([uiInputText``])} />
                        </label>
                      </div>
                    </div>
                    <div {...ui([uiFormRow`--full`, uiA`f-a-start`])}>
                      <span {...ui([uiFormRow`__label`, uiA`w-max-75 m-bottom`])}>Frequency</span>
                      <div {...ui([uiA`f f-1-1-auto f-gap-large`])}>
                        <label {...ui([uiFormRow``, uiA`f m-bottom-none f-gap-small f-0-0-125 font-weight-normal`])}>
                          <input
                            type="checkbox"
                            value="REPORTING_DATE"
                            checked={frequency === 'REPORTING_DATE'}
                            onChange={onSwitchFrequency}
                            disabled={!reportingDates?.length}
                            {...ui([uiCheckbox`--radio`, uiA`f-0-0-auto`])}
                          />
                          <span {...ui([uiFormRow`__label --free`])}>Reporting Date</span>
                        </label>
                        <label {...ui([uiFormRow``, uiA`f m-bottom-none f-gap-small m-right-auto font-weight-normal`])}>
                          <input
                            type="checkbox"
                            value="DAILY"
                            checked={['DAILY', 'WEEKLY', 'MONTHLY'].includes(frequency)}
                            onChange={onSwitchFrequency}
                            {...ui([uiCheckbox`--radio`])}
                          />
                          <span {...ui([uiFormRow`__label --free`])}>Custom</span>
                        </label>
                      </div>
                    </div>
                    <span {...ui([uiA`f f-gap f-column f-1-1-auto m-bottom`])}>
                      {['DAILY', 'WEEKLY', 'MONTHLY'].includes(frequency) && (
                        <>
                          <div {...ui([uiButtonSwitch`--light-blue --round`])}>
                            <button
                              type="button"
                              {...ui([uiButtonSwitch`__button --active:${frequency === 'DAILY'}`])}
                              value="DAILY"
                              onClick={onSwitchFrequency}
                            >
                              Daily
                            </button>
                            <button
                              type="button"
                              {...ui([uiButtonSwitch`__button --active:${frequency === 'WEEKLY'}`])}
                              value="WEEKLY"
                              onClick={onSwitchFrequency}
                            >
                              Weekly
                            </button>
                            <button
                              type="button"
                              {...ui([uiButtonSwitch`__button --active:${frequency === 'MONTHLY'}`])}
                              value="MONTHLY"
                              onClick={onSwitchFrequency}
                            >
                              Monthly
                            </button>
                          </div>
                        </>
                      )}
                      {frequency === 'WEEKLY' && (
                        <>
                          <label {...ui([uiA`f f-gap f-a-baseline m-bottom-none font-weight-normal`])}>
                            Every
                            <select {...ui([uiSelect``])} defaultValue={weeklyEvery} name="weeklyEvery">
                              {map('NovaScotia', (_, index) => (
                                <option value={index + 1} key={`admin-modals-request-sync-weekly-every-${index}`}>
                                  {index + 1}
                                </option>
                              ))}
                            </select>
                            week(s) on:
                          </label>
                          <div {...ui([uiButtonSwitch`--light-blue --round`])}>
                            {map('Alberta', (_, index) => (
                              <button
                                key={`admin-modals-request-sync-weekly-on-${index}`}
                                type="button"
                                onClick={onToggleWeeklyOn}
                                value={index}
                                {...ui([uiButtonSwitch`__button --active:${weeklyOn.includes(index)}`, uiA`p-x-9`])}
                              >
                                {moment().day(index).format('ddd')}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      {frequency === 'MONTHLY' && (
                        <>
                          <label {...ui([uiA`m-bottom-none f f-gap f-a-baseline font-weight-normal`])}>
                            Every
                            <select defaultValue={monthlyEvery} name="monthlyEvery" {...ui([uiSelect``])}>
                              {map('Saskatchewan', (_, index) => (
                                <option value={index + 1} key={['admin-modals-request-sync-monthly-every', index].join('-')}>
                                  {index + 1}
                                </option>
                              ))}
                            </select>
                            month(s) on:
                          </label>
                          <div {...ui([uiA`f f-gap-large`])}>
                            <label {...ui([uiFormRow``, uiA`f m-bottom-none f-gap-small w-75 font-weight-normal`])}>
                              <input
                                value="EACH"
                                type="checkbox"
                                checked={monthlyMode === 'EACH'}
                                onChange={onSwitchMonthlyMode}
                                {...ui([uiCheckbox`--radio`])}
                              />
                              <span {...ui([uiFormRow`__label`, uiA`f-1-1-auto`])}>Each</span>
                            </label>
                            <label {...ui([uiFormRow``, uiA`f m-bottom-none f-gap-small font-weight-normal`])}>
                              <input
                                value="ON_THE"
                                type="checkbox"
                                checked={monthlyMode === 'ON_THE'}
                                onChange={onSwitchMonthlyMode}
                                {...ui([uiCheckbox`--radio`])}
                              />
                              <span {...ui([uiFormRow`__label`, uiA`f-1-1-auto`])}>On The</span>
                            </label>
                          </div>
                          {monthlyMode === 'EACH' && (
                            <>
                              <div {...ui([uiCalendar`--light-blue`])}>
                                {map('PrinceEdwardIslandCharlottetown', (_, index) => (
                                  <button
                                    key={['admin-modals-request-sync-monthly-each-dates', index].join('-')}
                                    type="button"
                                    value={index + 1}
                                    onClick={onToggleMonthlyDates}
                                    {...ui([uiCalendar`__button --active:${monthlyDates.includes(index + 1)}`])}
                                  >
                                    {index + 1}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                          {monthlyMode === 'ON_THE' && (
                            <>
                              <div {...ui([uiA`f`])}>
                                <select {...ui([uiSelect`--stack-left`])} defaultValue={monthlyFirstLast} name="monthlyFirstLast">
                                  <option value="FIRST">First</option>
                                  <option value="LAST">Last</option>
                                </select>
                                <select defaultValue={monthlyDay} name="monthlyDay" {...ui([uiSelect`--stack-right`])}>
                                  {map('Manitoba', (_, index) => (
                                    <option
                                      key={['admin-modals-request-sync-monthly-on-the', index].join('-')}
                                      value={index ? upperCase(moment().day(index).format('dddd')) : 'DAY'}
                                    >
                                      {index ? moment().day(index).format('dddd') : 'day'}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {frequency === 'REPORTING_DATE' ? (
                        <div {...ui([uiA`f f-a-center f-gap`])}>
                          <input
                            defaultValue={reportingDays}
                            type="number"
                            min={0}
                            step={1}
                            name="reportingDays"
                            {...ui([uiInputText``, uiA`w-max-75`])}
                          />
                          <span {...ui([uiA`m-right-small`])}>Days</span>
                          <select defaultValue={reportingBeforeAfter} name="reportingDateBeforeAfter" {...ui([uiSelect``])}>
                            <option value="AFTER">After</option>
                            <option value="BEFORE">Before</option>
                          </select>
                          <span {...ui([uiA`line-height-normal`])}>
                            Reporting Period
                            <br />
                            End Date
                          </span>
                        </div>
                      ) : (
                        <div {...ui([uiA`f f-a-center`])}>
                          <select defaultValue={timeGenerated} name="timeGenerated" {...ui([uiSelect`--stack-left`])}>
                            {map('Newfoundland', (_, index) => (
                              <option key={['admin-modals-request-sync-time-generated', index].join('-')} value={index + 1}>{`${padStart(
                                index + 1,
                                2,
                                '0',
                              )}:00`}</option>
                            ))}
                          </select>
                          <select defaultValue={meridiem} name="meridiem" {...ui([uiSelect`--stack-right`, uiA`m-right`])}>
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                          <span {...ui([uiA`font-size-13 italic color-medium-gray`])}>{timeZoneName}</span>
                        </div>
                      )}
                    </span>
                  </div>
                  <p {...ui([uiFormRow``, uiA`m-x m-top`])}>Notification</p>
                  <div {...ui([uiA`f-0-0-auto`])}>
                    <ul {...ui([uiA`m-x p-none`])} style={{ listStyle: 'none' }}>
                      <li>
                        <label {...ui([uiFormRow``, uiA`f f-gap f-a-start font-weight-normal`])}>
                          <input
                            type="radio"
                            value="ERROR"
                            checked={notificationType === 'ERROR'}
                            name="notificationType"
                            onChange={onChangeNotificationType}
                            {...ui([uiCheckbox`--radio`])}
                          />
                          <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-350`])}>
                            Only when there are errors
                          </span>
                        </label>
                      </li>
                      <li>
                        <label {...ui([uiFormRow``, uiA`f f-gap f-a-start font-weight-normal`])}>
                          <input
                            type="radio"
                            value="ALWAYS"
                            name="notificationType"
                            checked={notificationType === 'ALWAYS'}
                            onChange={onChangeNotificationType}
                            {...ui([uiCheckbox`--radio`])}
                          />
                          <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-350`])}>Every Time</span>
                        </label>
                      </li>
                      <li>
                        <label {...ui([uiFormRow``, uiA`f f-gap f-a-start font-weight-normal`])}>
                          <input
                            type="radio"
                            value="NEVER"
                            checked={notificationType === 'NEVER'}
                            name="notificationType"
                            onChange={onChangeNotificationType}
                            {...ui([uiCheckbox`--radio`])}
                          />
                          <span {...ui([uiFormRow`__label`, uiA`font-weight-normal f-1-1-auto w-max-350`])}>Never</span>
                        </label>
                      </li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            <div {...ui([uiModal`__footer`, uiA`m-top-auto`])}>
              {(syncMode || autoSync) && (
                <button
                  type="submit"
                  disabled={updatingGeneralSettings} // Disable is true if loading state
                  {...ui([
                    uiButton`--red:--mint-blue:${(syncMode && withPublish) || (!syncMode && autoSync && autoPublish)} --disabled:${
                      !syncMode && !withChanges
                    } --loading:${updatingGeneralSettings}`, // --disabled if we have no changes but clickable to show overlay info, --loading if loading state
                    uiA`m-right m-left-auto`,
                  ])}
                >
                  <span {...ui([uiButton`__content`])}>
                    {syncMode
                      ? `Sync ${withPublish ? 'and Publish' : ''} Now`
                      : autoSync
                        ? `Schedule Sync${autoPublish ? ' and Publish' : ''}`
                        : 'Save'}
                  </span>
                </button>
              )}
              <button type="button" onClick={onClose} {...ui([uiButton`--default`, uiA`m-right m-left-auto:${!(syncMode || autoSync)}`])}>
                <span {...ui([uiButton`__content`])}>Cancel</span>
              </button>
            </div>
          </form>
        </>
      )}
    </ReactModal>
  );
}
